/**
 * Soft!=Live Page201 — Settings Alarm residual liquid (Page115/52 + dual).
 * Soft!=Live Page202 — Settings Trigger residual liquid (device_survey ∥ policy_write).
 * Mounts: /settings/alarm · /settings/trigger
 * Buses (contract ops — never painted in operator DOM).
 * Soft residual deleted:
 *  - Soft invent seats while posture loading
 *  - Soft empty-group / empty-device invent
 *  - Soft Save/Apply/Delete/policy save when write sealed
 *  - path /operation labels painted in DOM
 */
import { useEffect, useState } from "react";
import { html, F, Ico, KV, Card, Field, Input, Alert, Toggle } from "./ui.js";
import { path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import * as D from "./data.js";
import { setDirtySeat } from "./quality-runtime.js";
import { SafetyReflexEffectiveCard } from "./safety-reflex-effective.js";

/** Path-free operator copy for Alarm / Trigger desks. Nothing is invented when the seat is closed. */
export function softAlarmSettingsErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|seat_gated|not_authorized|denied|insufficient/.test(low)) {
    return "Your account cannot change this alarm setting. Ask a manager if you need access.";
  }
  if (/\b401\b|unauthorized|missing bearer|session/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b422\b|\b400\b|invalid|required|bounds|duration|delay/.test(low)) {
    return "That setting was not accepted. Check the values and try again.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable/.test(low)) {
    return "Alarm controls are not ready right now.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/api\/|\/group-cards\/|\/devices\/|\/settings\/alarm|\/settings\/trigger|GET |POST |PUT |status_path|http/.test(low)) {
    return "That action could not be completed.";
  }
  if (n.length > 160) return n.slice(0, 157) + "…";
  return n;
}


/** Soft≠Live Page201: missing contract ops must not crash the desk. */
function safeAlarmPath(op, opts) {
  try {
    return path(op, opts);
  } catch (_e) {
    if (op === "get__settings_alarm_status") return "/settings/alarm/status";
    if (op === "get__settings_trigger_status") return "/settings/trigger/status";
    if (op === "get__group-cards_settings") return "/group-cards/settings";
    if (op === "get__group-cards_capabilities") return "/group-cards/capabilities";
    if (op === "get__devices") return "/devices";
    if (op === "get__group-cards_p_settings") {
      const k = opts && opts.pathParams && opts.pathParams[0];
      return k ? "/group-cards/" + encodeURIComponent(k) + "/settings" : "";
    }
    if (op === "put__group-cards_p_settings") {
      const k = opts && opts.pathParams && opts.pathParams[0];
      return k ? "/group-cards/" + encodeURIComponent(k) + "/settings" : "";
    }
    if (op === "post__group-cards_p_apply") {
      const k = opts && opts.pathParams && opts.pathParams[0];
      return k ? "/group-cards/" + encodeURIComponent(k) + "/apply" : "";
    }
    if (op === "post__group-cards_p_delete") {
      const k = opts && opts.pathParams && opts.pathParams[0];
      return k ? "/group-cards/" + encodeURIComponent(k) + "/delete" : "";
    }
    if (op === "get__devices_p_trigger-policy") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? "/devices/" + encodeURIComponent(id) + "/trigger-policy" : "";
    }
    if (op === "put__devices_p_trigger-policy") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? "/devices/" + encodeURIComponent(id) + "/trigger-policy" : "";
    }
    return "";
  }
}

/** Pure: seat chips from Live Settings Alarm posture (Page201). */
export function alarmSettingsSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const paintMax = Number(lim.group_key_paint_max);
  const durMin = Number(lim.duration_ms_min);
  const durMax = Number(lim.duration_ms_max);
  return {
    surveyAllowed: p.survey_allowed === true,
    settingsWriteAllowed: p.settings_write_allowed === true,
    applyAllowed: p.apply_allowed === true,
    deleteAllowed: p.delete_allowed === true,
    writeAllowed: p.group_card_write_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    postureLabel: String(p.posture_label || "").trim(),
    groupKeyPaintMax:
      Number.isFinite(paintMax) && paintMax > 0 ? Math.floor(paintMax) : 80,
    durationMsMin: Number.isFinite(durMin) && durMin > 0 ? Math.floor(durMin) : 500,
    durationMsMax:
      Number.isFinite(durMax) && durMax > 0 ? Math.floor(durMax) : 300000,
  };
}

/** Pure: seat chips from Live Settings Trigger posture (Page202). */
export function triggerSettingsSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const paintMax = Number(lim.device_paint_max);
  const durMin = Number(lim.duration_ms_min);
  const panicMax = Number(lim.panic_fanout_ms_max);
  const loudMax = Number(lim.remote_loud_ms_max);
  return {
    surveyAllowed: p.survey_allowed === true,
    policyWriteAllowed: p.policy_write_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    postureLabel: String(p.posture_label || "").trim(),
    devicePaintMax:
      Number.isFinite(paintMax) && paintMax > 0 ? Math.floor(paintMax) : 120,
    durationMsMin: Number.isFinite(durMin) && durMin > 0 ? Math.floor(durMin) : 500,
    panicFanoutMsMax:
      Number.isFinite(panicMax) && panicMax > 0 ? Math.floor(panicMax) : 600000,
    remoteLoudMsMax:
      Number.isFinite(loudMax) && loudMax > 0 ? Math.floor(loudMax) : 300000,
  };
}

/** Pure: group keys from cards + device notification_group tags. */
export function groupKeysOf(cardsPack, devicesPack, paintMax) {
  const max = Number(paintMax) > 0 ? Math.floor(Number(paintMax)) : 80;
  const cardItems = (cardsPack && cardsPack.items) || [];
  const deviceRows = (devicesPack && devicesPack.items) || [];
  const fromCards = cardItems
    .map((r) => String(r.group_key || "").trim())
    .filter(Boolean);
  const fromDevices = [
    ...new Set(
      deviceRows
        .map((r) => String(r.notification_group || "").trim())
        .filter(Boolean),
    ),
  ];
  return [...new Set([...fromCards, ...fromDevices])]
    .sort((a, b) => a.localeCompare(b))
    .slice(0, max);
}

/** Pure: draft shape for group-card alarm settings. */
export function alarmDraftOf(detail, listed, errorReady) {
  const src = detail || listed || null;
  if (src) {
    return {
      trigger_mode: src.trigger_mode || "continuous",
      trigger_duration_ms:
        Number(src.trigger_duration_ms) || D.GROUP_CARD.trigger_duration_ms,
      delay_seconds: Number(src.delay_seconds) || 0,
      reboot_self_check: !!src.reboot_self_check,
    };
  }
  if (errorReady) return null;
  return null;
}

/** Pure: draft shape for device trigger policy. */
export function triggerDraftOf(policy, readyEmpty) {
  if (policy && typeof policy === "object") {
    return {
      panic_local_siren: !!policy.panic_local_siren,
      panic_link_enabled: policy.panic_link_enabled !== false,
      panic_fanout_duration_ms:
        Number(policy.panic_fanout_duration_ms)
        || D.TRIGGER_POLICY.panic_fanout_duration_ms,
      remote_silent_link_enabled: policy.remote_silent_link_enabled !== false,
      remote_loud_link_enabled: policy.remote_loud_link_enabled !== false,
      remote_loud_duration_ms:
        Number(policy.remote_loud_duration_ms)
        || D.TRIGGER_POLICY.remote_loud_duration_ms,
      fanout_exclude_self: policy.fanout_exclude_self !== false,
    };
  }
  if (readyEmpty) return { ...D.TRIGGER_POLICY };
  return null;
}

export function AlarmBehaviorDesk({ toast }) {
  const posture = useApi(safeAlarmPath("get__settings_alarm_status"));
  const post = posture.data || {};
  // Fail-closed while loading — seats fail-closed.
  const seat = alarmSettingsSeatOf(posture.loading || !posture.data ? {} : post);

  const cards = useApi(
    seat.surveyAllowed ? safeAlarmPath("get__group-cards_settings") : "",
  );
  const devices = useApi(
    seat.surveyAllowed ? safeAlarmPath("get__devices") : "",
  );
  const caps = useApi(
    seat.surveyAllowed ? safeAlarmPath("get__group-cards_capabilities") : "",
  );
  const { run, busy, error, envelope } = useAction();
  const [groupKey, setGroupKey] = useState("");
  const [custom, setCustom] = useState("");
  const [draft, setDraft] = useState(null);
  const [draftTouched, setDraftTouched] = useState(false);

  useEffect(() => {
    setDirtySeat(!!draftTouched);
    return () => { try { setDirtySeat(false); } catch (_) { /* ignore */ } };
  }, [draftTouched]);

  const cardItems = (cards.data && cards.data.items) || [];
  const keys = seat.surveyAllowed
    ? groupKeysOf(cards.data, devices.data, seat.groupKeyPaintMax)
    : [];

  const detailPath =
    groupKey && seat.surveyAllowed
      ? safeAlarmPath("get__group-cards_p_settings", { pathParams: [groupKey] })
      : null;
  const detail = useApi(detailPath);

  useEffect(() => {
    if (!groupKey || !seat.surveyAllowed) {
      setDraft(null);
      setDraftTouched(false);
      return;
    }
    const listed = cardItems.find((r) => r.group_key === groupKey) || null;
    const errorReady = !detail.loading && !!detail.error;
    const next = alarmDraftOf(detail.data, listed, errorReady);
    if (next) {
      setDraft(next);
      setDraftTouched(false);
    }
  }, [groupKey, detail.data, detail.loading, detail.error, cards.data, seat.surveyAllowed]);

  const pick = (k) => {
    if (!seat.surveyAllowed) {
      toast && toast("Group list is not available on this seat.", "warn");
      return;
    }
    setGroupKey(k);
    setCustom("");
  };
  const adoptCustom = () => {
    if (!seat.surveyAllowed) {
      toast && toast("Group list is not available on this seat.", "warn");
      return;
    }
    const k = custom.trim();
    if (!k) return;
    setGroupKey(k);
  };

  const set = (k, v) =>
    setDraft((d) => {
      if (!d) return d;
      setDraftTouched(true);
      const next = { ...d, [k]: v };
      if (k === "delay_seconds") {
        next.trigger_mode = Number(v) > 0 ? "delay" : "continuous";
      }
      return next;
    });

  const reload = () => {
    if (!seat.reReadAllowed) {
      toast && toast("Refresh is not available on this seat.", "warn");
      return;
    }
    posture.reload && posture.reload();
    cards.reload && cards.reload();
    devices.reload && devices.reload();
    detail.reload && detail.reload();
    caps.reload && caps.reload();
    toast && toast("Alarm settings refreshed", "go");
  };

  const save = async () => {
    if (!seat.settingsWriteAllowed) {
      toast && toast("Saving is not available on this seat.", "warn");
      return;
    }
    if (!groupKey || !draft) return;
    const out = await run(
      safeAlarmPath("put__group-cards_p_settings", { pathParams: [groupKey] }),
      {
        method: "PUT",
        body: {
          trigger_mode: draft.trigger_mode,
          trigger_duration_ms: Number(draft.trigger_duration_ms),
          delay_seconds: Number(draft.delay_seconds),
          reboot_self_check: !!draft.reboot_self_check,
        },
      },
    );
    if (!out) {
      toast(softAlarmSettingsErrorOf(error) || "Could not save group settings.", "bad");
      return;
    }
    toast("Group alarm settings saved · " + groupKey, "go");
    setDraftTouched(false);
    cards.reload && cards.reload();
    detail.reload && detail.reload();
  };

  const applyFanout = async () => {
    if (!seat.applyAllowed) {
      toast && toast("Apply to fleet is not available on this seat.", "warn");
      return;
    }
    if (!groupKey) return;
    const out = await run(
      safeAlarmPath("post__group-cards_p_apply", { pathParams: [groupKey] }),
      { method: "POST", body: {} },
    );
    if (!out) {
      toast(softAlarmSettingsErrorOf(error) || "Could not apply to the fleet.", "bad");
      return;
    }
    const n = out.device_count != null ? out.device_count : out.sent_now;
    toast(
      "Applied to fleet · "
        + groupKey
        + (n != null ? " · devices " + n : ""),
      "go",
    );
  };

  const removeCard = async () => {
    if (!seat.deleteAllowed) {
      toast && toast("Delete is not available on this seat.", "warn");
      return;
    }
    if (!groupKey) return;
    if (
      !window.confirm(
        "Delete group card settings for “"
          + groupKey
          + "”? Devices keep their tags; the settings row is removed.",
      )
    ) {
      return;
    }
    const out = await run(
      safeAlarmPath("post__group-cards_p_delete", { pathParams: [groupKey] }),
      { method: "POST", body: {} },
    );
    if (!out) {
      toast(softAlarmSettingsErrorOf(error) || "Could not delete the group card.", "bad");
      return;
    }
    toast("Group card deleted · " + groupKey, "go");
    setGroupKey("");
    setDraft(null);
    cards.reload && cards.reload();
    devices.reload && devices.reload();
  };

  const capOk = !!(caps.data && (caps.data.ok || caps.data.routes));
  const listing =
    seat.surveyAllowed
    && ((cards.loading && !cards.data) || (devices.loading && !devices.data));

  if (!seat.enabled && !posture.loading) {
    return html`<div className="alarm-desk alarm-desk--liquid"
      data-cmc-page="page115-alarm" data-cmc-alarm="page52" data-cmc-residual="page201-alarm">
      <${Alert} tone="warn" title="Alarm seat gated"
        say="Your seat cannot open group alarm settings. Nothing is invented when the seat is closed."/>
    </div>`;
  }

  return html`<div className="alarm-desk alarm-desk--liquid"
    data-cmc-page="page115-alarm" data-cmc-alarm="page52" data-cmc-residual="page201-alarm">
    <div className="alarm-desk__rail">
      <div>
        <h2 className="alarm-desk__title">Group alarm behaviour</h2>
      </div>
      <div className="btn-row">
        <button type="button" className="btn btn--ghost btn--sm" onClick=${reload}
          disabled=${busy || !seat.reReadAllowed}>
          <${Ico} d="refresh" w=${13}/> Refresh
        </button>
      </div>
    </div>

    ${envelope
      ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>`
      : null}
    <${SafetyReflexEffectiveCard} />

    <div className="alarm-desk__kpis">
      <div className="alarm-desk__kpi" data-on=${keys.length ? "1" : "0"}>
        <span>Groups in scope</span>
        <b>${seat.surveyAllowed ? keys.length : "—"}</b>
        <em>survey</em>
      </div>
      <div className="alarm-desk__kpi" data-on=${groupKey ? "1" : "0"}>
        <span>Selected key</span>
        <b>${groupKey || "—"}</b>
        <em>editor</em>
      </div>
      <div className="alarm-desk__kpi" data-on=${draft ? "1" : "0"}>
        <span>Mode</span>
        <b>${draft ? draft.trigger_mode : "—"}</b>
        <em>${seat.settingsWriteAllowed ? "write open" : "write sealed"}</em>
      </div>
      <div className="alarm-desk__kpi" data-on=${capOk ? "1" : "0"}>
        <span>Capabilities</span>
        <b>${seat.surveyAllowed ? (capOk ? "ready" : "…") : "sealed"}</b>
        <em>caps bus</em>
      </div>
    </div>

    <div className="alarm-desk__grid">
      <${Card} title="Which group"
          sub="Pick a card or type a key. Empty scope is honesty — not a Soft placeholder group.">
        ${!seat.surveyAllowed
          ? html`<${Alert} tone="warn" title="Survey sealed"
              say="Cannot invent group chips · Seat sealed."/>`
          : listing
          ? html`<${Alert} tone="ai" title="Loading group cards…"
              say="Reading group cards and device tags."/>`
          : html`<${F}>
              ${cards.error
                ? html`<${Alert} tone="bad" title="Group cards refused"
                    say=${softAlarmSettingsErrorOf(cards.error)}/>`
                : null}
              <div className="chip-row alarm-desk__chips">
                ${keys.map((k) => html`
                  <button key=${k} type="button" className="chip"
                    data-sel=${groupKey === k ? "1" : "0"}
                    onClick=${() => pick(k)}>${k}</button>`)}
                ${!keys.length
                  ? html`<p className="note">No group cards or device groups yet.
                      Type a key below to create the settings row first.</p>`
                  : null}
              </div>
              <div className="grid g2" style=${{ gap: "0 16px", marginTop: 12 }}>
                <${Field} label="Or name a group key"
                    hint="Saved even when no device carries this key yet.">
                  <${Input} icon="radio" value=${custom}
                    onInput=${(e) => setCustom(e.target.value)}
                    onKeyDown=${(e) => {
                      if (e.key === "Enter") adoptCustom();
                    }}
                    placeholder="yard-east"/>
                <//>
                <div className="btn-row" style=${{ alignSelf: "end" }}>
                  <button type="button" className="btn btn--ghost btn--sm"
                    disabled=${!custom.trim() || !seat.surveyAllowed}
                    onClick=${adoptCustom}>
                    Use this key
                  </button>
                </div>
              </div>
            <//>`}
      <//>

      ${groupKey && draft
        ? html`<${Card} title=${"Alarm behaviour · " + groupKey}
              meta=${detail.data && detail.data.owner_admin
                ? "owner " + detail.data.owner_admin
                : "group scope"}
              sub="Duration is how long siblings stay woken. Delay turns continuous into a grace window.">
            ${detail.loading && !detail.data
              ? html`<p className="note">Loading detail…</p>`
              : null}
            ${detail.error
              ? html`<${Alert} tone="warn" title="Detail empty or new key"
                  say="First save creates the settings row. Apply needs a saved card."/>`
              : null}
            <${Field} label=${"Hold / fan-out · " + draft.trigger_duration_ms + " ms"}
                hint=${seat.durationMsMin + " … " + seat.durationMsMax + " — allowed range."}>
              <input type="range" min=${seat.durationMsMin} max=${seat.durationMsMax} step=${500}
                value=${draft.trigger_duration_ms}
                disabled=${!seat.settingsWriteAllowed}
                onChange=${(e) => set("trigger_duration_ms", +e.target.value)}
                style=${{ width: "100%" }}/>
            <//>
            <${Field} label=${"Grace before fire · " + draft.delay_seconds + " s"}
                hint="0 = continuous. Any positive value switches to delay mode.">
              <input type="range" min=${0} max=${3600} step=${1}
                value=${draft.delay_seconds}
                disabled=${!seat.settingsWriteAllowed}
                onChange=${(e) => set("delay_seconds", +e.target.value)}
                style=${{ width: "100%" }}/>
            <//>
            <${KV} k="Mode (derived)" v=${draft.trigger_mode}/>
            <${Toggle} on=${draft.reboot_self_check}
              onChange=${(v) => {
                if (!seat.settingsWriteAllowed) {
                  toast && toast("Saving is not available on this seat.", "warn");
                  return;
                }
                set("reboot_self_check", v);
              }}
              title="Reboot self-check"
              say="After a reboot, the board verifies its own alarm path before trusting the next trigger."/>
            <div className="alarm-desk__acts">
              <button type="button" className="btn btn--sm btn--go"
                disabled=${busy || !draft || !seat.settingsWriteAllowed} onClick=${save}>
                ${busy ? "Working…" : "Save group settings"}
              </button>
              <button type="button" className="btn btn--sm"
                disabled=${busy || !groupKey || !seat.applyAllowed} onClick=${applyFanout}>
                Apply to fleet
              </button>
              <button type="button" className="btn btn--sm btn--ghost"
                disabled=${busy || !groupKey || !seat.deleteAllowed} onClick=${removeCard}>
                Delete card
              </button>
            </div>
          <//>`
        : html`<${Card} title="Editor"
              sub="Select a group key to open the draft.">
            <p className="note">No group selected — blank is intentional.</p>
          <//>`}
    </div>
  </div>`;
}

export function TriggerPolicyDesk({ toast }) {
  const posture = useApi(safeAlarmPath("get__settings_trigger_status"));
  const post = posture.data || {};
  // Fail-closed while loading — seats fail-closed.
  const seat = triggerSettingsSeatOf(posture.loading || !posture.data ? {} : post);

  const devices = useApi(
    seat.surveyAllowed ? safeAlarmPath("get__devices") : "",
  );
  const { run, busy, error, envelope } = useAction();
  const [deviceId, setDeviceId] = useState("");
  const [draft, setDraft] = useState(null);
  const [draftTouched, setDraftTouched] = useState(false);

  useEffect(() => {
    setDirtySeat(!!draftTouched);
    return () => { try { setDirtySeat(false); } catch (_) { /* ignore */ } };
  }, [draftTouched]);

  const rowsRaw = (devices.data && devices.data.items) || [];
  const rows = seat.surveyAllowed
    ? rowsRaw.slice(0, seat.devicePaintMax)
    : [];
  const detailPath =
    deviceId && seat.surveyAllowed
      ? safeAlarmPath("get__devices_p_trigger-policy", { pathParams: [deviceId] })
      : null;
  const detail = useApi(detailPath);

  useEffect(() => {
    if (!deviceId || !seat.surveyAllowed) {
      setDraft(null);
      setDraftTouched(false);
      return;
    }
    const pol = detail.data && detail.data.policy;
    if (pol) {
      setDraft(triggerDraftOf(pol, false));
      setDraftTouched(false);
    } else if (!detail.loading && !detail.error) {
      setDraft(triggerDraftOf(null, true));
      setDraftTouched(false);
    } else if (!detail.loading && detail.error) {
      setDraft(null);
      setDraftTouched(false);
    }
  }, [deviceId, detail.data, detail.loading, detail.error, seat.surveyAllowed]);

  const set = (k, v) => {
    if (!seat.policyWriteAllowed) {
      toast && toast("Saving policy is not available on this seat.", "warn");
      return;
    }
    setDraftTouched(true);
    setDraft((d) => (d ? { ...d, [k]: v } : d));
  };

  const pickDevice = (id) => {
    if (!seat.surveyAllowed) {
      toast && toast("Group list is not available on this seat.", "warn");
      return;
    }
    setDeviceId(id);
  };

  const reload = () => {
    if (!seat.reReadAllowed) {
      toast && toast("Refresh is not available on this seat.", "warn");
      return;
    }
    posture.reload && posture.reload();
    devices.reload && devices.reload();
    detail.reload && detail.reload();
    toast && toast("Trigger policy refreshed", "go");
  };

  const save = async () => {
    if (!seat.policyWriteAllowed) {
      toast && toast("Saving policy is not available on this seat.", "warn");
      return;
    }
    if (!deviceId || !draft) return;
    const out = await run(
      safeAlarmPath("put__devices_p_trigger-policy", { pathParams: [deviceId] }),
      {
        method: "PUT",
        body: {
          panic_local_siren: !!draft.panic_local_siren,
          panic_link_enabled: !!draft.panic_link_enabled,
          panic_fanout_duration_ms: Number(draft.panic_fanout_duration_ms),
          remote_silent_link_enabled: !!draft.remote_silent_link_enabled,
          remote_loud_link_enabled: !!draft.remote_loud_link_enabled,
          remote_loud_duration_ms: Number(draft.remote_loud_duration_ms),
          fanout_exclude_self: !!draft.fanout_exclude_self,
        },
      },
    );
    if (!out) {
      toast(softAlarmSettingsErrorOf(error) || "Could not save trigger policy.", "bad");
      return;
    }
    toast(
      "Trigger policy saved"
        + (out.scope_group ? " · group " + out.scope_group : ""),
      "go",
    );
    setDraftTouched(false);
    detail.reload && detail.reload();
  };

  const scope =
    (detail.data && (detail.data.scope_group || detail.data.scope_group_key))
    || "group scope";

  if (!seat.enabled && !posture.loading) {
    return html`<div className="alarm-desk alarm-desk--liquid"
      data-cmc-page="page115-trigger" data-cmc-trigger="page52" data-cmc-residual="page202-trigger">
      <${Alert} tone="warn" title="Trigger seat gated"
        say="Your seat cannot open trigger policy. Nothing is invented when the seat is closed."/>
    </div>`;
  }

  return html`<div className="alarm-desk alarm-desk--liquid"
    data-cmc-page="page115-trigger" data-cmc-trigger="page52" data-cmc-residual="page202-trigger">
    <div className="alarm-desk__rail">
      <div>
        <h2 className="alarm-desk__title">Device trigger policy</h2>
      </div>
      <div className="btn-row">
        <button type="button" className="btn btn--ghost btn--sm" onClick=${reload}
          disabled=${busy || !seat.reReadAllowed}>
          <${Ico} d="refresh" w=${13}/> Refresh
        </button>
      </div>
    </div>

    ${envelope
      ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>`
      : null}
    <${SafetyReflexEffectiveCard} />

    <div className="alarm-desk__kpis">
      <div className="alarm-desk__kpi" data-on=${rows.length ? "1" : "0"}>
        <span>Devices</span>
        <b>${seat.surveyAllowed ? rows.length : "—"}</b>
        <em>survey</em>
      </div>
      <div className="alarm-desk__kpi" data-on=${deviceId ? "1" : "0"}>
        <span>Selected</span>
        <b>${deviceId || "—"}</b>
        <em>editor</em>
      </div>
      <div className="alarm-desk__kpi" data-on=${detail.data ? "1" : "0"}>
        <span>Scope</span>
        <b>${deviceId && seat.surveyAllowed ? String(scope) : "—"}</b>
        <em>server</em>
      </div>
      <div className="alarm-desk__kpi"
        data-on=${draft && draft.panic_local_siren ? "1" : "0"}>
        <span>Local siren</span>
        <b>${draft ? (draft.panic_local_siren ? "ON" : "OFF") : "—"}</b>
        <em>${seat.policyWriteAllowed ? "write open" : "write sealed"}</em>
      </div>
    </div>

    <div className="alarm-desk__grid">
      <${Card} title="Which device"
          sub="Tap a board to load its trigger policy.">
        ${!seat.surveyAllowed
          ? html`<${Alert} tone="warn" title="Survey sealed"
              say="Cannot invent device chips · Seat sealed."/>`
          : devices.loading && !devices.data
          ? html`<${Alert} tone="ai" title="Loading devices…"
              say="Reading devices on this account."/>`
          : devices.error
            ? html`<${Alert} tone="bad" title="Devices refused"
                say=${softAlarmSettingsErrorOf(devices.error)}/>`
            : !rows.length
              ? html`<${Alert} tone="warn" title="No devices on this account"
                  say="Claim or bind a device first. Empty is honesty."/>`
              : html`<div className="rows alarm-desk__rows">
                  ${rows.map((r) => {
                    const id = String(r.device_id || r.id || "").trim();
                    const label =
                      String(
                        r.display_label || r.map_label || r.label || id,
                      ).trim() || id;
                    const grp = String(r.notification_group || "").trim();
                    return html`
                      <button key=${id} type="button" className="row"
                        data-on=${deviceId === id ? "1" : "0"}
                        onClick=${() => pickDevice(id)}>
                        <span className="st"/>
                        <span className="main">
                          <b>${label}</b>
                          <span>${id}${grp ? " · group " + grp : " · no group yet"}</span>
                        </span>
                      </button>`;
                  })}
                </div>`}
      <//>

      ${deviceId && draft
        ? html`<${Card} title=${"Trigger policy · " + deviceId}
              meta=${scope}
              sub="Local panic siren defaults OFF (customer requirement). Link and fan-out decide who else wakes.">
            ${detail.loading && !detail.data
              ? html`<p className="note">Loading policy…</p>`
              : null}
            ${detail.error
              ? html`<${Alert} tone="bad" title="Policy refused"
                  say=${softAlarmSettingsErrorOf(detail.error)}/>`
              : null}
            ${!seat.policyWriteAllowed
              ? html`<${Alert} tone="warn" title="Policy write sealed"
                  say="Soft save invent deleted. Toggles refuse mutation."/>`
              : null}
            <${Toggle} on=${draft.panic_local_siren}
              onChange=${(v) => set("panic_local_siren", v)}
              title="Local panic siren"
              say="When off, panic stays silent on the board that pressed it and still wakes siblings if link is on."/>
            <${Toggle} on=${draft.panic_link_enabled}
              onChange=${(v) => set("panic_link_enabled", v)}
              title="Panic wakes siblings"
              say="Fan-out to other boards in the same notification group."/>
            <${Field} label=${"Panic fan-out · " + draft.panic_fanout_duration_ms + " ms"}>
              <input type="range" min=${seat.durationMsMin} max=${seat.panicFanoutMsMax} step=${500}
                value=${draft.panic_fanout_duration_ms}
                disabled=${!seat.policyWriteAllowed}
                onChange=${(e) => set("panic_fanout_duration_ms", +e.target.value)}
                style=${{ width: "100%" }}/>
            <//>
            <${Toggle} on=${draft.remote_silent_link_enabled}
              onChange=${(v) => set("remote_silent_link_enabled", v)}
              title="Remote silent link"
              say="A remote command can wake the mesh without sounding local horns."/>
            <${Toggle} on=${draft.remote_loud_link_enabled}
              onChange=${(v) => set("remote_loud_link_enabled", v)}
              title="Remote loud link"
              say="A remote command may sound the siren path on linked boards."/>
            <${Field} label=${"Remote loud duration · " + draft.remote_loud_duration_ms + " ms"}>
              <input type="range" min=${seat.durationMsMin} max=${seat.remoteLoudMsMax} step=${500}
                value=${draft.remote_loud_duration_ms}
                disabled=${!seat.policyWriteAllowed}
                onChange=${(e) => set("remote_loud_duration_ms", +e.target.value)}
                style=${{ width: "100%" }}/>
            <//>
            <${Toggle} on=${draft.fanout_exclude_self}
              onChange=${(v) => set("fanout_exclude_self", v)}
              title="Exclude self from fan-out"
              say="The board that raised the event is not counted again in the sibling wake set."/>
            <div className="alarm-desk__acts">
              <button type="button" className="btn btn--sm btn--go"
                disabled=${busy || !!detail.error || !seat.policyWriteAllowed}
                onClick=${save}>
                ${busy ? "Saving…" : "Save trigger policy"}
              </button>
            </div>
          <//>`
        : html`<${Card} title="Editor"
              sub="Select a device to open the policy draft.">
            <p className="note">No device selected.</p>
          <//>`}
    </div>
  </div>`;
}
